import {
  Component,
  OnInit,
  OnDestroy,
  viewChild,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { forkJoin, Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { AuditDialogContainerComponent } from 'src/app/core/containers/audit-dialog-container.component';
import {
  LazyLoadFilters,
  PageRequest,
  SearchContainerComponent,
} from 'src/app/core/containers/search-container/search-container.component';
import { TabService } from 'src/app/core/services/tab.service';
import { Role } from 'src/app/model/admin/role';
import { AdminApiService } from 'src/app/services/api/admin-api.service';
import { AdminBuilderService } from 'src/app/services/builder/admin-builder.service';
import { LogAndMessageService } from 'src/app/services/log-and-message.service';
import { CLAIMS_ROLES_TABLE_DEFINITION } from 'src/app/admin/definitions/data-table-definitions';
import {
  Claim,
  ClaimConnection,
  UpdateClaimInput,
} from 'src/app/model/admin/claim';
import { isNullOrUndefined, sortByProperty } from 'src/app/utils/utils';
import { AuthApiService } from 'src/app/services/api/auth-api.service';
import { PickList } from '@openng/optimus-ui/picklist';
import { CLAIMS } from 'src/app/constants/auth-constants';
import { RoleTableService } from 'src/app/services/table/role-table.service';
import { GridLazyLoadEvent } from 'src/app/core/components/base-grid.component';
import { FilterMetadata, PrimeTemplate } from '@openng/optimus-ui/api';
import { BlockUI } from '@openng/optimus-ui/blockui';
import { ProgressSpinnerComponent } from '../../../../core/components/progress-spinner.component';
import { ButtonDirective } from '@openng/optimus-ui/button';
import { GeneralGridComponent } from '../../../../core/components/general-grid.component';
import { Dialog } from '@openng/optimus-ui/dialog';
import { InputText } from '@openng/optimus-ui/inputtext';

@Component({
  selector: 'app-claim-role-assign-container',
  templateUrl: './claim-role-assign-container.component.html',
  styleUrls: ['./claim-role-assign-container.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    BlockUI,
    ProgressSpinnerComponent,
    ButtonDirective,
    GeneralGridComponent,
    Dialog,
    FormsModule,
    ReactiveFormsModule,
    InputText,
    PickList,
    PrimeTemplate,
    TranslatePipe,
  ],
})
export class ClaimRoleAssignContainerComponent
  extends SearchContainerComponent<Role>
  implements OnInit, OnDestroy
{
  protected _adminBuilder = inject(AdminBuilderService);
  protected _adminApi = inject(AdminApiService);
  protected _translateService = inject(TranslateService);
  protected _logAndMessage = inject(LogAndMessageService);
  protected _tabService = inject(TabService);
  protected _roleTableService = inject(RoleTableService);
  protected _fb = inject(UntypedFormBuilder);
  protected _authApi = inject(AuthApiService);

  readonly audit = viewChild<AuditDialogContainerComponent>('audit');
  readonly pickList = viewChild<PickList>('pickList');

  canEdit = false;
  saving = false;
  claimRoleForm!: UntypedFormGroup;
  displayDialog = false;
  dialogHeader!: string;

  claimsAndRoles: any[] = [];
  selectedClaim?: Claim;
  showSuccessMessage = true;
  hasLoaded = false;

  activeTabChangedSub!: Subscription;

  availableRoles: Role[] = [];
  sourcePicklist: Role[] = [];
  targetPicklist: Role[] = [];

  constructor() {
    super();
  }

  ngOnInit() {
    this._roleTableService.setLastLazyLoad(null);
    this.setTableService(this._roleTableService);
    this.claimRoleForm = this._fb.group({
      id: [null],
      name: [{ value: null, disabled: true }, Validators.required],
      roles: [{ value: false, disabled: true }],
    });

    this.initialize();
  }

  ngOnDestroy(): void {
    if (this.activeTabChangedSub) {
      this.activeTabChangedSub.unsubscribe();
    }
  }

  initialize() {
    const screenName$ = this._translateService.get(
      'ROLE.SCREEN.CLAIM_ROLE_ASSIGN'
    );
    const dialogHeader$ = this._translateService.get(
      'ROLE.LABEL.CLAIM_DETAILS'
    );
    forkJoin([screenName$, dialogHeader$]).subscribe((label) => {
      this._tabService.updateActiveTabLabel(label[0]);
      this.screenName = label[0];
      this.dialogHeader = label[1];
    });

    this.canEdit = this._authApi.doesUserHaveAllClaimsFromList([
      CLAIMS.ADMINISTRATION.ROLES.EDIT_ROLES,
    ]);
    if (this.canEdit) {
      this.claimRoleForm.enable();
    }

    this.columns = CLAIMS_ROLES_TABLE_DEFINITION;
    this.applyPreviousData();
    this.applyPreviousFilters();
  }

  override handleLazyLoad(
    req: PageRequest,
    $event: GridLazyLoadEvent,
    filters: LazyLoadFilters
  ) {
    const query = {
      role: undefined,
      claim: undefined,
    };

    if (filters.name) {
      query.claim = (filters.name as FilterMetadata).value;
    }
    if (filters.roles) {
      query.role = (filters.roles as FilterMetadata).value;
    }

    this.queryNetwork(req, $event, query);
  }

  queryNetwork(
    req: PageRequest,
    $event: GridLazyLoadEvent,
    query: { [key: string]: any }
  ) {
    const sort = this.buildNetworkSortQuery($event, 'claim');
    this._roleTableService.setLastSortField('claim');
    this._roleTableService.setLastSortDirection(1);

    this._adminApi
      .queryForClaimsAndRoles(req.pageSize, req.page + 1, query, sort)
      .pipe(take(1))
      .subscribe({
        next: (data: ClaimConnection) => {
          this.claimsAndRoles = data.items;
          this.totalRecords = data.totalRecords;
          this.elements = [...this.claimsAndRoles];
          this.loading = false;
          this.hasLoaded = true;
        },
        error: (error) => {
          this._logAndMessage.errorLogOnly(error);
          this._logAndMessage.translateToErrorAlert({
            bodyKey: 'ADMIN.MESSAGES.ERROR.QUERY_ROLE_CLAIMS',
            headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
          });
          this.loading = false;
        },
      });
  }

  //Double clicking on table row action:
  changeViewEvent($event: { data: Claim }): void {
    this.selectedClaim = $event.data;
    this.claimRoleForm.patchValue({
      id: $event.data.id,
      name: $event.data.name,
      roles: $event.data.roles,
    });

    // Ensure that the name field is not editable
    this.claimRoleForm.controls['name'].disable();

    // update the sourceRoles
    // update the targetRoles
    this.sourcePicklist = [];
    this.targetPicklist = [];
    const roleClaims =
      this.claimRoleForm.controls['roles'].value
        ?.split(',')
        .map((x: string) => x.trim()) ?? [];

    this.availableRoles.forEach((x) => {
      if (roleClaims.includes(x.name)) {
        this.targetPicklist.push(x);
      } else {
        this.sourcePicklist.push(x);
      }
    });

    this.sourcePicklist.sort((a, b) => sortByProperty(a, b, 'name'));
    this.targetPicklist.sort((a, b) => sortByProperty(a, b, 'name'));
    this.displayDialog = true;
  }

  clearForm(): void {
    this.claimRoleForm.reset();
    this.pickList()?.resetSourceFilter();
    this.pickList()?.resetTargetFilter();
  }

  dialogClosed(): void {
    this.clearForm();
    this.selectedClaim = undefined;
    const audit = this.audit();
  }

  //refresh button in top right
  refresh(): void {
    this.lazyLoad({ lazy: this._roleTableService.getLastLazyLoad()! }); //this triggers infinite loop
  }

  //Save button in dialog popup
  saveClaim(): void {
    if (!this.canEdit) {
      return;
    }

    if (!isNullOrUndefined(this.claimRoleForm) && this.claimRoleForm.valid) {
      const updateClaimInput: UpdateClaimInput | null =
        this._adminBuilder.buildClaimRoleInput(
          this.claimRoleForm.getRawValue(),
          this.targetPicklist
        );

      this.saving = true;

      this._adminApi
        .updateClaim(updateClaimInput)
        .pipe(take(1))
        .subscribe({
          next: (result: Claim) => {
            let updatedClaim = this.updateClaimsAndRoles(result);
            this.changeViewEvent({ data: updatedClaim });
            this.clearForm();
            this.displayDialog = false;
            this.saving = false;
          },
          error: (error) => {
            this.saving = false;
            console.error('Error updating claim.', error);
          },
        });
    }
  }

  updateClaimsAndRoles(claim: any): Claim {
    // find the role in the list
    let index = this.claimsAndRoles.findIndex((x) => x.id == claim.id);
    this.claimsAndRoles[index].roles = claim.roles
      .map((r: Role) => r.name)
      .join(', ');
    return this.claimsAndRoles[index];
  }
}
